import React, { useState, useRef } from 'react';
import { ArrowUpRight, ArrowRight, ArrowLeft, ShieldCheck, FileText, ClipboardList, Handshake, CheckCircle2, AlertCircle, Mail, Phone, Paperclip, X, Upload, Plus, Trash2 } from 'lucide-react';
import { COMPANY } from '../data/content';
import PageHero from '../components/PageHero';
import { submitSubcontractor } from '../services/firestoreService';

const STEPS = [
  { n: '01', t: 'Express Interest', d: 'Send a brief company profile and the trade you want to register for. Our procurement team responds within 5 working days.' },
  { n: '02', t: 'Submit Documents', d: 'Complete the registration form below and attach the required documents.' },
  { n: '03', t: 'Capability Review', d: 'Site visit and reference checks. Our engineering team validates technical capability, manpower strength and safety record.' },
  { n: '04', t: 'Empanelment', d: 'Approved partners are added to the Tricolite vendor panel and invited to bid on relevant scopes of work.' },
];

// File uploads — gstinCert is mandatory (marked with required:true)
const FILE_FIELDS = [
  { key: 'gstinCert',        label: 'GSTIN Certificate Copy *',                 required: true  },
  { key: 'certIncorporation',label: 'Certificate of Incorporation',             required: false },
  { key: 'isoCerts',         label: 'ISO 9001 / 14001 / 18001 Certificates',    required: false },
  { key: 'performanceCert',  label: 'Performance Certificates (if any)',        required: false },
  { key: 'balanceSheets',    label: 'Last 3 Years Balance Sheet',               required: false },
  { key: 'companyProfile',   label: 'Company Profile',                          required: false },
];

const TRADE_OPTIONS = ['Electrician', 'Welder', 'Fitter', 'Helper'];

// New section order (per client, Jan 2026):
// 01 Company  →  02 Documents  →  03 Partner  →  04 Escalation
// →  05 Employees  →  06 References  →  07 Compliance
const FORM_SECTIONS = [
  { id: 'company',     num: '01', title: 'Company Details' },
  { id: 'documents',   num: '02', title: 'Document Uploads' },
  { id: 'partner',     num: '03', title: 'Partner / Contact Details' },
  { id: 'escalation',  num: '04', title: 'Escalation Matrix' },
  { id: 'employees',   num: '05', title: 'Employee Details' },
  { id: 'references',  num: '06', title: 'References & Experience' },
  { id: 'compliance',  num: '07', title: 'Compliance & Certifications' },
];

const emptyEmployee = () => ({ name: '', aadhaar: '', trade: '' });
const emptyReference = () => ({ client: '', scope: '', value: '', contact: '', phone: '' });

export default function Subcontractors() {
  const [form, setForm] = useState({
    companyName: '', bankDetails: '', gstinNumber: '',
    ownerName: '', orgStructure: '', establishedIn: '', companyType: '',
    partnerName: '', partnerAddress: '', partnerMobile: '', partnerEmail: '',
    escalationMatrix: '',
    permanentEmployees: [emptyEmployee()],
    contractualEmployees: [emptyEmployee()],
    references: [emptyReference(), emptyReference(), emptyReference()],
    toolsAndTackle: '', pfEsic: '', pastExperience: '',
  });
  const [files, setFiles] = useState({});
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [errors, setErrors] = useState({});
  const fileRefs = useRef({});
  const formTopRef = useRef(null);

  const set = (k) => (e) => {
    const v = e.target.value;
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(er => ({ ...er, [k]: undefined }));
  };

  const handleFile = (key) => (e) => {
    const file = e.target.files[0];
    if (file) {
      setFiles(f => ({ ...f, [key]: file }));
      if (errors[`file_${key}`]) setErrors(er => ({ ...er, [`file_${key}`]: undefined }));
    }
  };

  const removeFile = (key) => {
    setFiles(f => { const n = { ...f }; delete n[key]; return n; });
    if (fileRefs.current[key]) fileRefs.current[key].value = '';
  };

  // ── Permanent employees (dynamic rows) ─────────────────────────────────────
  const updateEmp = (i, field, val) =>
    setForm(f => ({ ...f, permanentEmployees: f.permanentEmployees.map((e, idx) => idx === i ? { ...e, [field]: val } : e) }));
  const addEmp = () => setForm(f => ({ ...f, permanentEmployees: [...f.permanentEmployees, emptyEmployee()] }));
  const removeEmp = (i) => setForm(f => ({ ...f, permanentEmployees: f.permanentEmployees.length > 1 ? f.permanentEmployees.filter((_, idx) => idx !== i) : f.permanentEmployees }));

  // ── Contractual employees (same shape, all optional) ───────────────────────
  const updateContractor = (i, field, val) =>
    setForm(f => ({ ...f, contractualEmployees: f.contractualEmployees.map((e, idx) => idx === i ? { ...e, [field]: val } : e) }));
  const addContractor = () => setForm(f => ({ ...f, contractualEmployees: [...f.contractualEmployees, emptyEmployee()] }));
  const removeContractor = (i) => setForm(f => ({ ...f, contractualEmployees: f.contractualEmployees.length > 1 ? f.contractualEmployees.filter((_, idx) => idx !== i) : f.contractualEmployees }));

  // ── References (fixed 3 rows, can grow) ────────────────────────────────────
  const updateRef = (i, field, val) =>
    setForm(f => ({ ...f, references: f.references.map((r, idx) => idx === i ? { ...r, [field]: val } : r) }));
  const addRef = () => setForm(f => ({ ...f, references: [...f.references, emptyReference()] }));
  const removeRef = (i) => setForm(f => ({ ...f, references: f.references.length > 3 ? f.references.filter((_, idx) => idx !== i) : f.references }));

  // ── Per-step validation ────────────────────────────────────────────────────
  const validateStep = (idx) => {
    const id = FORM_SECTIONS[idx].id;
    const er = {};
    if (id === 'company') {
      if (!form.companyName.trim()) er.companyName = 'Required';
      if (!form.gstinNumber.trim()) er.gstinNumber = 'Required';
      if (!form.ownerName.trim()) er.ownerName = 'Required';
    }
    if (id === 'documents') {
      FILE_FIELDS.forEach(ff => { if (ff.required && !files[ff.key]) er[`file_${ff.key}`] = 'Required'; });
    }
    if (id === 'partner') {
      if (!form.partnerName.trim()) er.partnerName = 'Required';
      if (!form.partnerMobile.trim()) er.partnerMobile = 'Required';
      if (!form.partnerEmail.trim()) er.partnerEmail = 'Required';
    }
    if (id === 'employees') {
      const valid = form.permanentEmployees.filter(e => e.name.trim() && e.trade.trim());
      if (valid.length < 1) er.permanentEmployees = 'At least one permanent employee with name & trade is required';
    }
    if (id === 'references') {
      const filled = form.references.filter(r => r.client.trim() && r.scope.trim() && r.contact.trim());
      if (filled.length < 3) er.references = 'Minimum 3 recent references with Client, Scope and Contact are required';
    }
    if (id === 'compliance') {
      if (!form.pfEsic) er.pfEsic = 'Required';
    }
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const goNext = () => {
    if (!validateStep(stepIdx)) return;
    setStepIdx(i => Math.min(FORM_SECTIONS.length - 1, i + 1));
    formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const goPrev = () => {
    setStepIdx(i => Math.max(0, i - 1));
    setErrors({});
    formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validateStep(stepIdx)) return;
    setBusy(true);
    setStatus(null);
    try {
      // Serialize structured rows into readable strings so backend & admin views stay simple
      const permanentStr = form.permanentEmployees
        .filter(emp => emp.name.trim())
        .map(emp => `${emp.name} — Aadhaar: ${emp.aadhaar || '—'} — Trade: ${emp.trade || '—'}`).join('\n');
      const contractualStr = form.contractualEmployees
        .filter(emp => emp.name.trim())
        .map(emp => `${emp.name} — Aadhaar: ${emp.aadhaar || '—'} — Trade: ${emp.trade || '—'}`).join('\n');
      const referencesStr = form.references
        .filter(r => r.client.trim() || r.scope.trim())
        .map((r, i) => `Reference ${i + 1}: ${r.client} — ${r.scope} — Value: ${r.value || '—'} — Contact: ${r.contact || '—'} (${r.phone || '—'})`).join('\n');

      const payload = {
        ...form,
        permanentEmployees: permanentStr,
        contractualEmployees: contractualStr,
        references: referencesStr,
      };

      await submitSubcontractor(payload, files);
      setStatus('ok');
      setForm({
        companyName: '', bankDetails: '', gstinNumber: '',
        ownerName: '', orgStructure: '', establishedIn: '', companyType: '',
        partnerName: '', partnerAddress: '', partnerMobile: '', partnerEmail: '',
        escalationMatrix: '',
        permanentEmployees: [emptyEmployee()],
        contractualEmployees: [emptyEmployee()],
        references: [emptyReference(), emptyReference(), emptyReference()],
        toolsAndTackle: '', pfEsic: '', pastExperience: '',
      });
      setFiles({});
      setStepIdx(0);
    } catch {
      setStatus('err');
    } finally { setBusy(false); }
  };

  const currentSection = FORM_SECTIONS[stepIdx];
  const isLast = stepIdx === FORM_SECTIONS.length - 1;

  return (
    <main>
      <PageHero
        eyebrow="Become a Subcontractor"
        title="Build with us —"
        accent="safely, fairly, on time."
        image="/img/sub-hero.jpg"
        desc="Tricolite Energy partners with specialist subcontractors across electrical, civil, solar and MEP trades. Our goal: an injury-free site, fair payment terms, and a long term total quality win-win relationship."
      />

      {/* Approach */}
      <section id="approach" className="px-6 md:px-10 lg:px-14 py-20 lg:py-28 bg-[var(--cream)]" data-testid="approach-section">
        <div className="max-w-[1500px] mx-auto grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5 reveal">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--orange)] font-semibold mb-4">Our Approach</div>
            <h2 className="font-display text-[clamp(32px,4vw,56px)] leading-[1.05] font-bold tracking-tight">
              "Our House" —<br/>
              <span className="font-serif-it text-[var(--orange)]">a fair place to work.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-[16px] md:text-[17px] text-[var(--text-mid)] leading-[1.85] reveal">
            <p>At Tricolite Energy, we believe a project is only as strong as the partners who build it. We foster a caring environment where every subcontractor, vendor and tradesperson has a sense of belonging — included, respected and paid on time.</p>
            <p>Our goal is to provide our subcontractors with an <b className="text-[var(--ink)]">injury-free, safe work environment</b> that supports a healthy and more equitable future for our people, our business, and the planet we work on.</p>
            <p>Through it all, we hold the highest standards of <b className="text-[var(--ink)]">integrity, ISO-aligned process and TQM discipline</b> — because that's how we deliver the results our customers expect, project after project.</p>
          </div>
        </div>
        <div className="max-w-[1500px] mx-auto mt-16 grid md:grid-cols-3 gap-5">
          {[
            { i: ShieldCheck, t: 'Safety First', d: 'TQM-driven safety culture. Site-specific JSAs, daily toolbox talks and zero-tolerance for unsafe acts.' },
            { i: Handshake, t: 'Fair Terms', d: 'Clear scopes, milestone-based billing and on-time releases. No retentions held without reason.' },
            { i: CheckCircle2, t: 'Long-term Partnership', d: 'We grow with partners who grow with us. Repeat work, capability uplift and reference letters that open doors.' },
          ].map((c, i) => (
            <div key={i} className="bg-white border border-[var(--line)] rounded-2xl p-7 hover-lift reveal" style={{ transitionDelay: `${i*70}ms` }}>
              <c.i className="w-7 h-7 text-[var(--orange)] mb-5"/>
              <h3 className="font-display font-bold text-[20px] mb-3">{c.t}</h3>
              <p className="text-[14.5px] text-[var(--text-mid)] leading-[1.8]">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Steps */}
      <section id="register" className="px-6 md:px-10 lg:px-14 py-20 lg:py-28 bg-[var(--cream-deep)]" data-testid="register-section">
        <div className="max-w-[1500px] mx-auto">
          <div className="reveal mb-14 max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--orange)] font-semibold mb-4">How to Register</div>
            <h2 className="font-display text-[clamp(30px,3.8vw,52px)] leading-[1.05] font-bold tracking-tight">
              Four steps to <span className="font-serif-it text-[var(--orange)]">empanelment.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {STEPS.map((s, i) => (
              <div key={i} className="group bg-white border border-[var(--line)] rounded-3xl p-7 md:p-8 hover-lift reveal" style={{ transitionDelay: `${i*70}ms` }}>
                <div className="flex items-center justify-between mb-7">
                  <span className="font-display font-bold text-[36px] text-[var(--orange)] leading-none">{s.n}</span>
                  <ArrowRight className="w-5 h-5 text-[var(--text-light)] group-hover:text-[var(--orange)] group-hover:translate-x-1 transition-all"/>
                </div>
                <h3 className="font-display font-bold text-[20px] mb-3 leading-tight">{s.t}</h3>
                <p className="text-[14px] text-[var(--text-mid)] leading-[1.8]">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ REGISTRATION FORM (multi-step inline wizard) ═══════ */}
      <section id="form" className="px-6 md:px-10 lg:px-14 py-20 lg:py-28 bg-[var(--cream)]" data-testid="registration-form-section">
        <div className="max-w-[1100px] mx-auto" ref={formTopRef}>
          <div className="reveal mb-10 text-center">
            <h2 className="font-display text-[clamp(30px,3.8vw,52px)] leading-[1.05] font-bold tracking-tight">
              Sub Contractor <span className="font-serif-it text-[var(--orange)]">Registration Form</span>
            </h2>
            <p className="mt-5 text-[15.5px] text-[var(--text-mid)] max-w-2xl mx-auto">Complete this form step by step and attach the required documents. Our procurement team will review and respond within 5 working days.</p>
          </div>

          {status === 'ok' ? (
            <div className="bg-white border border-[var(--line)] rounded-3xl p-10 text-center reveal" data-testid="form-success">
              <CheckCircle2 className="w-14 h-14 text-[var(--orange)] mx-auto mb-4"/>
              <h3 className="font-display font-bold text-[26px] mb-3">Registration Received</h3>
              <p className="text-[16px] text-[var(--text-mid)] max-w-xl mx-auto leading-[1.8]">Thank you for your interest in registering as a subcontractor with Tricolite Energy Solutions. Your application has been received and is under review. Our procurement team will revert with the next steps shortly.</p>
              <button onClick={() => setStatus(null)} className="mt-8 inline-flex items-center gap-2 text-[13px] uppercase tracking-wider font-semibold text-[var(--ink)] hover:text-[var(--orange)] transition-colors" data-testid="submit-another">Submit another <ArrowRight className="w-4 h-4"/></button>
            </div>
          ) : (
            <form onSubmit={submit} className="reveal" data-testid="sub-registration-form" noValidate>

              {/* Step indicator — clickable to jump backwards only */}
              <StepIndicator sections={FORM_SECTIONS} stepIdx={stepIdx} onJump={(i) => { if (i < stepIdx) { setStepIdx(i); setErrors({}); } }} />

              {/* Active section card */}
              <div className="mt-8">
                <FormCard num={currentSection.num} title={currentSection.title}>

                  {currentSection.id === 'company' && (
                    <>
                      <div className="grid md:grid-cols-2 gap-4">
                        <FI label="Name of Company *" value={form.companyName} onChange={set('companyName')} placeholder="Registered company name" required error={errors.companyName} testid="field-companyName"/>
                        <FI label="GSTIN Number *" value={form.gstinNumber} onChange={set('gstinNumber')} placeholder="e.g. 06AABCU9603R1ZM" required error={errors.gstinNumber} testid="field-gstinNumber"/>
                      </div>
                      <FI label="Bank Details" value={form.bankDetails} onChange={set('bankDetails')} placeholder="Account no., IFSC, Bank & Branch name" testid="field-bankDetails"/>
                      <div className="grid md:grid-cols-2 gap-4">
                        <FI label="Name of Owner *" value={form.ownerName} onChange={set('ownerName')} placeholder="Full name of proprietor / director" required error={errors.ownerName} testid="field-ownerName"/>
                        <FI label="Organization Structure" value={form.orgStructure} onChange={set('orgStructure')} placeholder="e.g. Pvt Ltd, Partnership, Proprietorship" testid="field-orgStructure"/>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <FI label="Established In" value={form.establishedIn} onChange={set('establishedIn')} placeholder="Year of incorporation" testid="field-establishedIn"/>
                        <FI label="Type of Company" value={form.companyType} onChange={set('companyType')} placeholder="e.g. Electrical Contractor, Civil, MEP" testid="field-companyType"/>
                      </div>
                    </>
                  )}

                  {currentSection.id === 'documents' && (
                    <>
                      <p className="text-[13px] text-[var(--text-mid)] mb-4">Upload the required documents. Accepted formats: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG (max 10MB each). Fields marked with * are mandatory.</p>
                      <div className="grid md:grid-cols-2 gap-4">
                        {FILE_FIELDS.map(ff => (
                          <div key={ff.key}>
                            <label className="block text-[12px] uppercase tracking-wider text-[var(--text-light)] mb-2 font-medium">{ff.label}</label>
                            <input
                              ref={el => fileRefs.current[ff.key] = el}
                              type="file"
                              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                              className="hidden"
                              onChange={handleFile(ff.key)}
                              data-testid={`file-${ff.key}`}
                            />
                            {files[ff.key] ? (
                              <div className="flex items-center gap-2 p-3 rounded-xl bg-[var(--cream-deep)] border border-[var(--line)]">
                                <Paperclip className="w-3.5 h-3.5 text-[var(--orange)] shrink-0"/>
                                <span className="text-[13px] text-[var(--ink)] truncate flex-1">{files[ff.key].name}</span>
                                <span className="text-[11px] text-[var(--text-mid)] shrink-0">{(files[ff.key].size / 1024).toFixed(0)} KB</span>
                                <button type="button" onClick={() => removeFile(ff.key)} className="w-6 h-6 rounded-full bg-white border border-[var(--line)] flex items-center justify-center hover:border-red-400 shrink-0">
                                  <X className="w-3 h-3 text-[var(--text-mid)]"/>
                                </button>
                              </div>
                            ) : (
                              <button type="button" onClick={() => fileRefs.current[ff.key]?.click()} className={`w-full flex items-center gap-2 px-3 py-3 rounded-xl border border-dashed bg-[var(--cream-deep)] hover:border-[var(--orange)] text-[13px] hover:text-[var(--orange)] transition-colors ${errors[`file_${ff.key}`] ? 'border-red-400 text-red-600' : 'border-[var(--line)] text-[var(--text-mid)]'}`} data-testid={`btn-file-${ff.key}`}>
                                <Upload className="w-3.5 h-3.5"/> {errors[`file_${ff.key}`] ? 'Required — choose file' : 'Choose file'}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {currentSection.id === 'partner' && (
                    <>
                      <div className="grid md:grid-cols-2 gap-4">
                        <FI label="Partner / Contact Person Name *" value={form.partnerName} onChange={set('partnerName')} placeholder="Full name" required error={errors.partnerName} testid="field-partnerName"/>
                        <FI label="Mobile No. * (WhatsApp)" value={form.partnerMobile} onChange={set('partnerMobile')} placeholder="+91 — also used for WhatsApp updates" required error={errors.partnerMobile} testid="field-partnerMobile"/>
                      </div>
                      <FI label="Email ID *" value={form.partnerEmail} onChange={set('partnerEmail')} placeholder="email@company.com" type="email" required error={errors.partnerEmail} testid="field-partnerEmail"/>
                      <FI label="Address" value={form.partnerAddress} onChange={set('partnerAddress')} placeholder="Registered office address" textarea testid="field-partnerAddress"/>
                    </>
                  )}

                  {currentSection.id === 'escalation' && (
                    <FI label="Escalation Matrix" value={form.escalationMatrix} onChange={set('escalationMatrix')} placeholder="Level 1: Name / Phone / Email&#10;Level 2: Name / Phone / Email&#10;Level 3: Name / Phone / Email" textarea testid="field-escalationMatrix"/>
                  )}

                  {currentSection.id === 'employees' && (
                    <>
                      <div className="mb-4">
                        <div className="flex items-baseline justify-between mb-3">
                          <h4 className="font-display font-semibold text-[15px] text-[var(--ink)]">Permanent Employees *</h4>
                          <span className="text-[12px] text-[var(--text-light)]">Min 1 required (with Name & Trade)</span>
                        </div>
                        <div className="space-y-3">
                          {form.permanentEmployees.map((emp, i) => (
                            <div key={i} className="grid md:grid-cols-[1fr_1fr_180px_auto] gap-3 items-end p-4 rounded-2xl border border-[var(--line)] bg-[var(--cream-deep)]" data-testid={`emp-row-${i}`}>
                              <div>
                                <label className="block text-[11px] uppercase tracking-wider text-[var(--text-light)] mb-1 font-medium">Name {i === 0 && '*'}</label>
                                <input value={emp.name} onChange={(e) => updateEmp(i, 'name', e.target.value)} className="cf-input" placeholder="Full name" data-testid={`emp-name-${i}`}/>
                              </div>
                              <div>
                                <label className="block text-[11px] uppercase tracking-wider text-[var(--text-light)] mb-1 font-medium">Aadhaar No.</label>
                                <input value={emp.aadhaar} onChange={(e) => updateEmp(i, 'aadhaar', e.target.value)} className="cf-input" placeholder="12-digit Aadhaar" data-testid={`emp-aadhaar-${i}`}/>
                              </div>
                              <div>
                                <label className="block text-[11px] uppercase tracking-wider text-[var(--text-light)] mb-1 font-medium">Trade {i === 0 && '*'}</label>
                                <select value={emp.trade} onChange={(e) => updateEmp(i, 'trade', e.target.value)} className="cf-input" data-testid={`emp-trade-${i}`}>
                                  <option value="">Select…</option>
                                  {TRADE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                              </div>
                              <button type="button" onClick={() => removeEmp(i)} disabled={form.permanentEmployees.length <= 1} className="h-[42px] w-[42px] rounded-full bg-white border border-[var(--line)] flex items-center justify-center hover:border-red-400 disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`emp-remove-${i}`} aria-label="Remove employee">
                                <Trash2 className="w-4 h-4 text-[var(--text-mid)]"/>
                              </button>
                            </div>
                          ))}
                        </div>
                        <button type="button" onClick={addEmp} className="mt-3 inline-flex items-center gap-2 text-[13px] font-medium text-[var(--orange)] hover:text-[var(--orange-soft)]" data-testid="emp-add">
                          <Plus className="w-4 h-4"/> Add employee
                        </button>
                        {errors.permanentEmployees && <p className="mt-2 text-[12px] text-red-600">{errors.permanentEmployees}</p>}
                      </div>

                      <div className="mt-6 pt-6 border-t border-[var(--line)]">
                        <div className="flex items-baseline justify-between mb-3">
                          <h4 className="font-display font-semibold text-[15px] text-[var(--ink)]">Contractual Employees</h4>
                          <span className="text-[12px] text-[var(--text-light)]">Optional</span>
                        </div>
                        <div className="space-y-3">
                          {form.contractualEmployees.map((emp, i) => (
                            <div key={i} className="grid md:grid-cols-[1fr_1fr_180px_auto] gap-3 items-end p-4 rounded-2xl border border-[var(--line)] bg-[var(--cream-deep)]" data-testid={`contractor-row-${i}`}>
                              <div>
                                <label className="block text-[11px] uppercase tracking-wider text-[var(--text-light)] mb-1 font-medium">Name</label>
                                <input value={emp.name} onChange={(e) => updateContractor(i, 'name', e.target.value)} className="cf-input" placeholder="Full name" data-testid={`contractor-name-${i}`}/>
                              </div>
                              <div>
                                <label className="block text-[11px] uppercase tracking-wider text-[var(--text-light)] mb-1 font-medium">Aadhaar No.</label>
                                <input value={emp.aadhaar} onChange={(e) => updateContractor(i, 'aadhaar', e.target.value)} className="cf-input" placeholder="12-digit Aadhaar" data-testid={`contractor-aadhaar-${i}`}/>
                              </div>
                              <div>
                                <label className="block text-[11px] uppercase tracking-wider text-[var(--text-light)] mb-1 font-medium">Trade</label>
                                <select value={emp.trade} onChange={(e) => updateContractor(i, 'trade', e.target.value)} className="cf-input" data-testid={`contractor-trade-${i}`}>
                                  <option value="">Select…</option>
                                  {TRADE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                              </div>
                              <button type="button" onClick={() => removeContractor(i)} disabled={form.contractualEmployees.length <= 1} className="h-[42px] w-[42px] rounded-full bg-white border border-[var(--line)] flex items-center justify-center hover:border-red-400 disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`contractor-remove-${i}`} aria-label="Remove contractor">
                                <Trash2 className="w-4 h-4 text-[var(--text-mid)]"/>
                              </button>
                            </div>
                          ))}
                        </div>
                        <button type="button" onClick={addContractor} className="mt-3 inline-flex items-center gap-2 text-[13px] font-medium text-[var(--orange)] hover:text-[var(--orange-soft)]" data-testid="contractor-add">
                          <Plus className="w-4 h-4"/> Add contractor
                        </button>
                      </div>
                    </>
                  )}

                  {currentSection.id === 'references' && (
                    <>
                      <div className="mb-4">
                        <div className="flex items-baseline justify-between mb-3">
                          <h4 className="font-display font-semibold text-[15px] text-[var(--ink)]">Top 3 Recent References *</h4>
                          <span className="text-[12px] text-[var(--text-light)]">All 3 required (Client, Scope & Contact)</span>
                        </div>
                        <div className="space-y-3">
                          {form.references.map((r, i) => (
                            <div key={i} className="p-4 rounded-2xl border border-[var(--line)] bg-[var(--cream-deep)] space-y-3" data-testid={`ref-row-${i}`}>
                              <div className="flex items-center justify-between">
                                <span className="text-[11px] uppercase tracking-wider text-[var(--orange)] font-semibold">Reference {i + 1} {i < 3 && '*'}</span>
                                {i >= 3 && (
                                  <button type="button" onClick={() => removeRef(i)} className="text-[12px] text-[var(--text-mid)] hover:text-red-600 inline-flex items-center gap-1" data-testid={`ref-remove-${i}`}>
                                    <Trash2 className="w-3 h-3"/> Remove
                                  </button>
                                )}
                              </div>
                              <div className="grid md:grid-cols-2 gap-3">
                                <input value={r.client} onChange={(e) => updateRef(i, 'client', e.target.value)} className="cf-input" placeholder="Client Name *" data-testid={`ref-client-${i}`}/>
                                <input value={r.value} onChange={(e) => updateRef(i, 'value', e.target.value)} className="cf-input" placeholder="Value (₹)" data-testid={`ref-value-${i}`}/>
                              </div>
                              <input value={r.scope} onChange={(e) => updateRef(i, 'scope', e.target.value)} className="cf-input" placeholder="Project Scope of Work *" data-testid={`ref-scope-${i}`}/>
                              <div className="grid md:grid-cols-2 gap-3">
                                <input value={r.contact} onChange={(e) => updateRef(i, 'contact', e.target.value)} className="cf-input" placeholder="Contact Person *" data-testid={`ref-contact-${i}`}/>
                                <input value={r.phone} onChange={(e) => updateRef(i, 'phone', e.target.value)} className="cf-input" placeholder="Phone" data-testid={`ref-phone-${i}`}/>
                              </div>
                            </div>
                          ))}
                        </div>
                        <button type="button" onClick={addRef} className="mt-3 inline-flex items-center gap-2 text-[13px] font-medium text-[var(--orange)] hover:text-[var(--orange-soft)]" data-testid="ref-add">
                          <Plus className="w-4 h-4"/> Add another reference
                        </button>
                        {errors.references && <p className="mt-2 text-[12px] text-red-600">{errors.references}</p>}
                      </div>

                      <FI label="List of Tools, Tackle & Safety Items Owned" value={form.toolsAndTackle} onChange={set('toolsAndTackle')} placeholder="Equipment / Tool — Qty — Condition&#10;(include safety equipment owned)" textarea testid="field-toolsAndTackle"/>
                      <FI label="Past Experience with TES" value={form.pastExperience} onChange={set('pastExperience')} placeholder="Describe any past work done with Tricolite Energy Solutions, if applicable" textarea testid="field-pastExperience"/>
                    </>
                  )}

                  {currentSection.id === 'compliance' && (
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[12px] uppercase tracking-wider text-[var(--text-light)] mb-2 font-medium">PF, ESIC Registered? *</label>
                        <select value={form.pfEsic} onChange={set('pfEsic')} className={`cf-input ${errors.pfEsic ? 'border-red-400' : ''}`} data-testid="pf-esic-select">
                          <option value="">Select…</option>
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                          <option value="In Progress">In Progress</option>
                        </select>
                        {errors.pfEsic && <p className="mt-1.5 text-[12px] text-red-600">{errors.pfEsic}</p>}
                      </div>
                    </div>
                  )}

                </FormCard>
              </div>

              {/* Navigation */}
              <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
                <button type="button" onClick={goPrev} disabled={stepIdx === 0 || busy} className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[var(--line)] bg-white text-[14px] font-medium text-[var(--ink)] hover:border-[var(--orange)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-testid="btn-prev">
                  <ArrowLeft className="w-4 h-4"/> Previous
                </button>

                <div className="text-[13px] text-[var(--text-mid)]">
                  Step <span className="font-semibold text-[var(--ink)]">{stepIdx + 1}</span> of {FORM_SECTIONS.length}
                </div>

                {isLast ? (
                  <button type="submit" disabled={busy} className="inline-flex items-center gap-2 bg-[var(--orange)] hover:bg-[var(--orange-soft)] disabled:opacity-60 text-white pl-6 pr-7 py-3.5 rounded-full font-medium transition-colors" data-testid="submit-registration">
                    {busy ? 'Submitting…' : 'Submit Registration'} <ArrowUpRight className="w-4 h-4"/>
                  </button>
                ) : (
                  <button type="button" onClick={goNext} className="inline-flex items-center gap-2 bg-[var(--orange)] hover:bg-[var(--orange-soft)] text-white pl-6 pr-7 py-3.5 rounded-full font-medium transition-colors" data-testid="btn-next">
                    Next <ArrowRight className="w-4 h-4"/>
                  </button>
                )}
              </div>

              {Object.keys(errors).length > 0 && (
                <div className="mt-4 flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900" data-testid="step-errors">
                  <AlertCircle className="w-5 h-5 mt-0.5 shrink-0"/>
                  <div className="text-[13.5px] leading-relaxed">Please complete the required fields highlighted on this step before continuing.</div>
                </div>
              )}

              {isLast && (
                <div className="mt-5 flex items-start gap-2 text-[13px] text-[var(--text-mid)]">
                  <AlertCircle className="w-4 h-4 text-[var(--orange)] mt-0.5 shrink-0"/>
                  <span>By submitting, you confirm the information is accurate. Our procurement team will review and respond within 5 working days.</span>
                </div>
              )}

              {status === 'err' && (
                <div className="mt-4 flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-red-800">
                  <AlertCircle className="w-5 h-5 mt-0.5"/>
                  <div className="text-[14px]">Could not submit right now. Please try again or email {COMPANY.email} directly.</div>
                </div>
              )}
            </form>
          )}
        </div>
      </section>

      {/* Contact strip */}
      <section className="px-6 md:px-10 lg:px-14 py-14 bg-[var(--ink)] text-white relative overflow-hidden" data-nav-theme="dark">
        <div className="absolute inset-0 grid-bg-dark opacity-30"/>
        <div className="relative max-w-[1500px] mx-auto grid md:grid-cols-3 gap-8 items-center">
          <div className="relative md:col-span-1">
            <ClipboardList className="w-7 h-7 text-[var(--orange)] mb-4"/>
            <h3 className="font-display font-bold text-[24px] mb-2" style={{color:'#fff'}}>Procurement Desk</h3>
            <p className="text-[14px] text-white/65">Direct line to our subcontractor empanelment team.</p>
          </div>
          <div className="relative flex items-start gap-3">
            <Mail className="w-5 h-5 text-[var(--orange)] mt-1 shrink-0"/>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-white/50 mb-1">Email</div>
              <a href={`mailto:${COMPANY.email}`} className="text-[15px] font-medium link-u" style={{color:'#fff'}}>{COMPANY.email}</a>
            </div>
          </div>
          <div className="relative flex items-start gap-3">
            <Phone className="w-5 h-5 text-[var(--orange)] mt-1 shrink-0"/>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-white/50 mb-1">Phone</div>
              <a href={`tel:${COMPANY.phoneTel}`} className="text-[15px] font-medium link-u" style={{color:'#fff'}}>{COMPANY.phonePrimary}</a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function StepIndicator({ sections, stepIdx, onJump }) {
  return (
    <div className="bg-white border border-[var(--line)] rounded-3xl p-4 md:p-5" data-testid="step-indicator">
      <div className="flex items-center gap-1 md:gap-2 overflow-x-auto pb-1">
        {sections.map((s, i) => {
          const isActive = i === stepIdx;
          const isDone = i < stepIdx;
          const isClickable = i < stepIdx;
          return (
            <React.Fragment key={s.id}>
              <button
                type="button"
                onClick={() => isClickable && onJump(i)}
                disabled={!isClickable}
                className={`flex items-center gap-2 shrink-0 px-3 py-2 rounded-full text-[12px] font-semibold transition-colors ${
                  isActive ? 'bg-[var(--orange)] text-white'
                  : isDone ? 'bg-[var(--orange)]/10 text-[var(--orange)] hover:bg-[var(--orange)]/20 cursor-pointer'
                  : 'bg-[var(--cream-deep)] text-[var(--text-light)] cursor-not-allowed'
                }`}
                data-testid={`step-${s.num}`}
              >
                <span className={`flex items-center justify-center w-5 h-5 rounded-full text-[11px] ${isActive ? 'bg-white/25' : isDone ? 'bg-[var(--orange)] text-white' : 'bg-white border border-[var(--line)]'}`}>
                  {isDone ? <CheckCircle2 className="w-3.5 h-3.5"/> : s.num}
                </span>
                <span className="hidden md:inline whitespace-nowrap">{s.title}</span>
              </button>
              {i < sections.length - 1 && <div className={`hidden md:block flex-1 h-px ${isDone ? 'bg-[var(--orange)]/40' : 'bg-[var(--line)]'}`}/>}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function FormCard({ num, title, children }) {
  return (
    <div className="bg-white border border-[var(--line)] rounded-3xl p-7 md:p-9 space-y-4" data-testid={`form-card-${num}`}>
      <div className="flex items-center gap-3 mb-2">
        <span className="font-display font-bold text-[14px] text-[var(--orange)]">{num}</span>
        <h3 className="font-display font-bold text-[20px]">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function FI({ label, value, onChange, placeholder, required, type, textarea, error, testid }) {
  return (
    <div>
      <label className="block text-[12px] uppercase tracking-wider text-[var(--text-light)] mb-2 font-medium">{label}</label>
      {textarea ? (
        <textarea value={value} onChange={onChange} className={`cf-textarea ${error ? 'border-red-400' : ''}`} placeholder={placeholder} rows={4} data-testid={testid}/>
      ) : (
        <input type={type || 'text'} value={value} onChange={onChange} className={`cf-input ${error ? 'border-red-400' : ''}`} placeholder={placeholder} data-testid={testid}/>
      )}
      {error && <p className="mt-1.5 text-[12px] text-red-600">{error}</p>}
    </div>
  );
}
